/**
 * Particle content normalization for the compose editor (Phase 1 editor swap).
 *
 * Stored draft content historically came in three shapes:
 *   1. EditorJS block JSON — an array of blocks, or editor.save()'s OutputData
 *      wrapper ({ time, blocks, version }). In this codebase nearly every block
 *      is `{ type: 'html', data: { html } }`; very old drafts may hold classic
 *      paragraph/header/list blocks.
 *   2. A plain HTML string (synced emails, replies/forwards).
 *   3. Empty (blank new draft).
 *
 * Going forward drafts store HTML only; these helpers migrate legacy shapes on
 * read and manage the per-user signature marker inside the HTML.
 */

// Type-only: a value import of the @newgenus/common barrel would run
// every-moment's extendMoment side effect, which throws under jest.
import type { FileMetadata } from '@newgenus/common';

export interface LegacyEditorBlock {
    type: string;
    data: { html?: string; text?: string; level?: number; style?: string; items?: string[] };
    id?: string;
}

const BLANK_SCAFFOLD = '<span></span>';

/** `{uid}-signature` — the marker id carried over from the EditorJS signature block. */
export function signatureId(uid: string): string {
    return `${uid}-signature`;
}

function tryParseBlocks(content: string): LegacyEditorBlock[] | null {
    let parsed: unknown;
    try {
        parsed = JSON.parse(content);
    } catch {
        return null;
    }
    if (Array.isArray(parsed)) return parsed as LegacyEditorBlock[];
    if (parsed && typeof parsed === 'object' && Array.isArray((parsed as { blocks?: unknown }).blocks)) {
        return (parsed as { blocks: LegacyEditorBlock[] }).blocks;
    }
    return null;
}

function blockToHtml(block: LegacyEditorBlock): string {
    const data = block.data ?? {};

    switch (block.type) {
        case 'html': {
            const html = data.html ?? '';
            const body = html === BLANK_SCAFFOLD ? BLANK_LINE : html;
            // The signature block is identified by its id — keep it addressable.
            return block.id ? `<div id="${block.id}">${html}</div>` : body;
        }
        case 'paragraph':
            return `<p>${data.text ?? ''}</p>`;
        case 'header':
            return `<h${data.level ?? 1}>${data.text ?? ''}</h${data.level ?? 1}>`;
        case 'list': {
            const tag = data.style === 'ordered' ? 'ol' : 'ul';
            const items = (data.items ?? []).map((i) => `<li>${i}</li>`).join('');
            return `<${tag}>${items}</${tag}>`;
        }
        default:
            return data.html ?? data.text ?? '';
    }
}

export function blocksToHtml(blocks: LegacyEditorBlock[]): string {
    return blocks.map(blockToHtml).join('\n');
}

/**
 * Normalize any stored particle content to an HTML string.
 * Returns null for empty content so the caller can scaffold a blank draft
 * (blank lines + signature).
 */
export function normalizeParticleContentToHtml(content: string | null | undefined): string | null {
    if (!content || content.trim().length === 0) return null;

    const blocks = tryParseBlocks(content);
    if (blocks) return blocksToHtml(blocks);

    return content;
}

// A div, not a <p>: the compose editor's line model is single-spaced divs
// (forced_root_block 'div'). The caret opens inside this scaffold, and Enter
// SPLITS the current block — a p-scaffold would keep minting margined
// paragraphs no matter what forced_root_block says.
const BLANK_LINE = '<div><br></div>';

/** True when the stored content is legacy EditorJS block JSON (array or OutputData). */
export function isLegacyBlocksContent(content: string | null | undefined): boolean {
    if (!content) return false;
    return tryParseBlocks(content) !== null;
}

/**
 * Build the html a compose session opens with.
 *
 * - Blank draft: blank lines to type into, signature below.
 * - Existing draft (legacy blocks or html): migrated/kept as-is — it already
 *   carries its own signature.
 * - Reply/forward: blank lines + signature on top, the quoted mail below.
 */
export function buildInitialComposeHtml(args: {
    content: string | null | undefined;
    isReplyOrForward: boolean;
    uid: string;
    signatureHtml: string;
}): string {
    const scaffold = [BLANK_LINE, BLANK_LINE, BLANK_LINE].join('\n');
    const scaffoldWithSignature = args.signatureHtml
        ? `${scaffold}\n<div id="${signatureId(args.uid)}">${args.signatureHtml}</div>`
        : scaffold;

    const existing = normalizeParticleContentToHtml(args.content);
    if (existing === null) return scaffoldWithSignature;

    // A legacy-blocks draft is an in-progress draft (it already carries its
    // own scaffold/signature) — even when the route action says reply/forward.
    if (args.isReplyOrForward && !isLegacyBlocksContent(args.content)) {
        return `${scaffoldWithSignature}\n${existing}`;
    }
    return existing;
}

/**
 * Rewrite locally-pasted inline images (blob: object URLs) to cid: references
 * so the stored/sent html points at the attachment instead of a dead local
 * URL. Images without an id have no attachment to reference and are left as-is.
 */
export function replaceBlobUrlsWithCid(html: string): string {
    const container = document.createElement('div');
    container.innerHTML = html;

    const blobImages = Array.from(container.querySelectorAll('img')).filter(
        (img) => img.getAttribute('src')?.startsWith('blob:') && img.id
    );
    if (blobImages.length === 0) return html;

    blobImages.forEach((img) => img.setAttribute('src', `cid:${img.id}`));
    return container.innerHTML;
}

/** Every content id the html references as an inline image (`src="cid:…"`). */
function collectCidReferences(html: string): Set<string> {
    const container = document.createElement('div');
    container.innerHTML = html || '';
    return new Set(
        Array.from(container.querySelectorAll('img'))
            .map((img) => img.getAttribute('src') || '')
            .filter((src) => src.startsWith('cid:'))
            .map((src) => src.slice('cid:'.length))
    );
}

/**
 * Stamp `contendId`/`isInline` meta for every attachment the content references
 * as a cid: inline image, so the read view and a reopened draft can hydrate it
 * (`InlineImageService.hydrate` keys on `contendId`). Compose-uploaded files
 * default to `location: 'storage'`. Returns a NEW map — store-sourced maps are
 * frozen.
 */
export function stampInlineAttachmentMeta(
    html: string,
    attachments: string[],
    attachmentsMeta: Record<string, FileMetadata> | undefined
): Record<string, FileMetadata> {
    const meta: Record<string, FileMetadata> = { ...(attachmentsMeta || {}) };
    const referencedCids = collectCidReferences(html);

    (attachments || [])
        .filter((name) => referencedCids.has(name))
        .forEach((name) => {
            const existing = meta[name];
            meta[name] = {
                attachmentId: '',
                size: 0,
                mimeType: '',
                location: 'storage',
                ...(existing || {}),
                filename: name,
                contendId: name,
                isInline: true,
            };
        });

    return meta;
}

/**
 * Drop inline attachments whose image was deleted from the content. The send
 * builders key inline treatment on the body reference, so an isInline
 * attachment nothing references would go out as a PLAIN attachment — a file
 * the user thought they removed. Send-time only: during editing an undo can
 * restore the image, and the attachment must still exist for it. Returns a
 * NEW array/map (store-sourced state is frozen).
 */
export function pruneUnreferencedInlineAttachments(
    html: string,
    attachments: string[],
    attachmentsMeta: Record<string, FileMetadata> | undefined
): { attachments: string[]; attachmentsMeta: Record<string, FileMetadata> } {
    const meta = attachmentsMeta || {};
    const referencedCids = collectCidReferences(html);

    const keep = (name: string): boolean => {
        const entry = meta[name];
        if (!entry?.isInline) return true;
        return referencedCids.has(entry.contendId || name);
    };

    return {
        attachments: (attachments || []).filter(keep),
        attachmentsMeta: Object.fromEntries(Object.entries(meta).filter(([name]) => keep(name))),
    };
}

/**
 * Coerce any particle content shape (legacy blocks JSON string, in-memory
 * OutputData object, html string, empty) to the one stored shape: html string.
 */
export function ensureHtmlContent(content: unknown): string {
    if (content === null || content === undefined) return '';
    const asString = typeof content === 'string' ? content : JSON.stringify(content);
    return normalizeParticleContentToHtml(asString) ?? '';
}
